import API_URLS from '../config/apiConfig';
import api from '../interceptors/axiosConfig';
import { ApiError, apiDelete, apiGet, apiPost } from '../utils/apiUtils';
import { authApiRequest } from './authService/authApiRequest';
import { getStoreOwnerStores, getStoreOwners, getStores } from './authService/authStores';
import type { SyncGamesToPosPayload } from '../types/features/storeDetails/overview';
import type {
  ActiveGamesAndPacksResponse,
  ActivatedPacksResponse,
  AgentConfig,
  AgentParams,
  AuthResponse,
  InviteStoreUserPayload,
  InviteStoreUserResponse,
  ManualLottoSalesEntryPayload,
  ManualPayoutEntryPayload,
  OTPResponse,
  Store,
  StoreLogsResponse,
  StoreLocation,
  StoreLocationCreatePayload,
  StoreLocationUpdatePayload,
  StoreOwnerDetailsResponse,
  StoreOwnersListResponse,
  StoresListResponse,
  StoreUser,
  TokenData,
  UpdateAgentParamsPayload,
  UploadAgentLogsPayload,
  User,
} from '../types/services/auth';
import { normalizeIdToInt, normalizePriceToFloat } from '../utils/normalizers/auditTicketNormalizers';

export type {
  ActiveGame,
  ActiveGamePack,
  ActivatedPack,
  InviteStoreUserPayload,
  InviteStoreUserResponse,
  ManualEntryResponse,
  SaveAgentConfigPayload,
  SaveAgentConfigResponse,
  StoreLogFile,
  StoreOwnerStore,
  TicketInfo,
  UpdateStorePayload,
  UploadAgentLogsResponse,
} from '../types/services/auth';

export type {
  ActiveGamesAndPacksResponse,
  ActivatedPacksResponse,
  AgentConfig,
  AgentParams,
  AuthResponse,
  ManualLottoSalesEntryPayload,
  ManualPayoutEntryPayload,
  OTPResponse,
  Store,
  StoreLogsResponse,
  StoreLocation,
  StoreLocationCreatePayload,
  StoreLocationUpdatePayload,
  StoreOwner,
  StoreOwnerDetailsResponse,
  StoreOwnersListResponse,
  StoresListResponse,
  StoreUser,
  UpdateAgentParamsPayload,
  UploadAgentLogsPayload,
  User,
} from '../types/services/auth';

class AuthService {
  // Token management
  private setTokens(tokenData: TokenData) {
    
    let expiresAt: number;
    
    if (tokenData.expires_at) {
      // API returns expires_at as ISO string
      expiresAt = new Date(tokenData.expires_at).getTime();
    } else {
      // Default to 30 minutes if expires_at is not provided
      expiresAt = Date.now() + (30 * 60 * 1000);
    }
    
    localStorage.setItem('accessToken', tokenData.access_token);
    localStorage.setItem('refreshToken', tokenData.refresh_token);
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());
    
  }

  // Get current access token (synchronous)
  getAccessToken(): string | null {
    const token = localStorage.getItem('accessToken');
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    
    if (!token || !expiresAt) {
      return null;
    }

    const now = Date.now();
    const expiryTime = parseInt(expiresAt);
    const isExpired = now > expiryTime;
    
    if (isExpired) {
      return null;
    }

    return token;
  }

  // Get current access token (async) - no refresh attempts
  async getValidAccessToken(): Promise<string | null> {
    return this.getAccessToken();
  }

  // Request OTP from lotto-reco API
  async sendOTP(identifier: string, options?: { call?: boolean; authorizeOtp?: boolean }): Promise<OTPResponse> {
    try {
      await authApiRequest(
        API_URLS.REQUEST_OTP,
        {
          method: 'POST',
          body: {
            identifier,
            ...(options?.authorizeOtp && { authorize_otp: true }),
            ...(options?.call && { call: true }),
          },
        }
      );

      return {
        success: true,
        message: 'OTP sent successfully',
      };
    } catch (error) {
      console.error('Error sending OTP:', error);
      if (error instanceof ApiError) {
        return {
          success: false,
          message: error.message || 'Failed to send OTP',
        };
      }
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  // Verify OTP with lotto-reco API
  async verifyOTP(identifier: string, otp: string): Promise<AuthResponse> {
    try {
      const data = await authApiRequest(
        API_URLS.VERIFY_OTP,
        {
          method: 'POST',
          body: { 
            identifier,
            otp: otp 
          },
        }
      );

      // Store tokens and user info
      this.setTokens(data);
      localStorage.setItem('user', JSON.stringify({
        identifier,
        token: data.access_token,
      }));

      let user: User = await this.authMeAndGetUserDetails();

      localStorage.setItem('userDetails', JSON.stringify(user));

      return {
        success: true,
        message: 'Login successful',
        token: data.access_token,
        refreshToken: data.refresh_token,
        user: { identifier }
      };
    } catch (error) {
      console.error('Error verifying OTP:', error);
      if (error instanceof ApiError) {
        return {
          success: false,
          message: error.message || 'Invalid OTP',
        };
      }
      return {
        success: false,
        message: 'Network error. Please try again.',
      };
    }
  }

  // Get stores (optionally paginated and filtered)
  async getStores(params?: { 
    subscription_status?: string | null; 
    store_status?: string | null; 
    search?: string | null;
    get_all?: boolean;
    page?: number;          // 1-based
    page_size?: number;
  }): Promise<StoresListResponse> {
    try {
      return await getStores(params);
    } catch (error) {
      console.error('Error fetching stores:', error);
      throw error;
    }
  }

  async getStoreOwners(params?: {
    page?: number;
    page_size?: number;
    search?: string | null;
    include_without_stores?: boolean;
    active_subscription_only?: boolean;
    exclude_active_subscriptions?: boolean;
    phone_verified_only?: boolean;
  }): Promise<StoreOwnersListResponse> {
    try {
      return await getStoreOwners(params);
    } catch (error) {
      console.error('Error fetching store owners:', error);
      throw error;
    }
  }

  async getStoreOwnerStores(ownerId: string): Promise<StoreOwnerDetailsResponse> {
    try {
      return await getStoreOwnerStores(ownerId);
    } catch (error) {
      console.error('Error fetching owner stores:', error);
      throw error;
    }
  }

  async deleteUserWithoutStores(userId: string): Promise<void> {
    try {
      await apiDelete(API_URLS.DELETE_USER_WITHOUT_STORES(userId));
    } catch (error) {
      console.error('Error deleting user without stores:', error);
      throw error;
    }
  }

  async getStoreDetails(storeId: string): Promise<Store> {
    try {
      const data = await apiGet(API_URLS.GET_STORES + `${storeId}`);
      return data || [];
    } catch (error) {
      console.error('Error fetching stores:', error);
      throw error;
    }
  }

  async submitManualLottoSalesEntry(storeId: string, payload: ManualLottoSalesEntryPayload): Promise<any> {
    try {
      return await apiPost(API_URLS.MANUAL_LOTTO_SALES_ENTRY(storeId), payload);
    } catch (error) {
      console.error('Error submitting manual lotto sales entry:', error);
      throw error;
    }
  }

  async submitManualPayoutEntry(storeId: string, payload: ManualPayoutEntryPayload): Promise<any> {
    try {
      return await apiPost(API_URLS.MANUAL_PAYOUT_ENTRY(storeId), payload);
    } catch (error) {
      console.error('Error submitting manual payout entry:', error);
      throw error;
    }
  }

  // Get store users
  async getStoreUsers(storeId: string): Promise<StoreUser[]> {
    try {
      const data = await apiGet(API_URLS.GET_STORE_USERS(storeId));
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching store users:', error);
      throw error;
    }
  }

  async getStoreLocations(storeId: string): Promise<StoreLocation[]> {
    try {
      const data = await apiGet(API_URLS.GET_STORE_LOCATIONS(storeId));
      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error('Error fetching store locations:', error);
      throw error;
    }
  }

  async createStoreLocation(storeId: string, payload: StoreLocationCreatePayload): Promise<StoreLocation> {
    try {
      return await apiPost(API_URLS.CREATE_STORE_LOCATION(storeId), payload);
    } catch (error) {
      console.error('Error creating store location:', error);
      throw error;
    }
  }

  async updateStoreLocation(storeId: string, locationId: string, payload: StoreLocationUpdatePayload): Promise<StoreLocation> {
    try {
      const response = await api({
        method: 'PATCH',
        url: API_URLS.UPDATE_STORE_LOCATION(storeId, locationId),
        data: payload,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating store location:', error);
      throw error;
    }
  }

  // Invite a user to the store
  async inviteStoreUser(storeId: string, payload: InviteStoreUserPayload): Promise<InviteStoreUserResponse> {
    try {
      const response = await api({
        method: 'POST',
        url: API_URLS.INVITE_STORE_USER(storeId),
        data: payload,
      });
      return response.data;
    } catch (error) {
      console.error('Error inviting store user:', error);
      throw error;
    }
  }

  async addSelfAsHiddenManager(storeId: string): Promise<StoreUser> {
    try {
      return await apiPost(API_URLS.ADD_SELF_AS_HIDDEN_MANAGER(storeId));
    } catch (error) {
      console.error('Error adding self as hidden manager:', error);
      throw error;
    }
  }

  async removeStoreUser(storeId: string, userId: string): Promise<void> {
    try {
      await apiDelete(API_URLS.REMOVE_STORE_USER(storeId, userId));
    } catch (error) {
      console.error('Error removing store user:', error);
      throw error;
    }
  }

  async updateStoreUserVisibility(storeId: string, userId: string, isHidden: boolean): Promise<StoreUser> {
    try {
      const response = await api({
        method: 'PATCH',
        url: API_URLS.UPDATE_STORE_USER_VISIBILITY(storeId, userId),
        data: { is_hidden: isHidden },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating store user visibility:', error);
      throw error;
    }
  }

  async updateStoreUserDailyReconciliationEmail(storeId: string, userId: string, enabled: boolean): Promise<StoreUser> {
    try {
      const response = await api({
        method: 'PATCH',
        url: API_URLS.UPDATE_STORE_USER_DAILY_RECONCILIATION_EMAIL(storeId, userId),
        data: { enabled },
      });
      return response.data;
    } catch (error) {
      console.error('Error updating daily reconciliation email preference:', error);
      throw error;
    }
  }

  // Get agent configuration
  async getAgentConfig(storeId: string): Promise<AgentConfig> {
    try {
      const data = await apiGet(API_URLS.GET_AGENT_CONFIG(storeId));
      return data;
    } catch (error) {
      console.error('Error fetching agent config:', error);
      throw error;
    }
  }

  // Save / update agent configuration
  async saveAgentConfig(storeId: string, configBody: any): Promise<any> {
    try {
      const response = await api({
        method: 'PUT',
        url: API_URLS.SAVE_AGENT_CONFIG(storeId),
        data: configBody,
      });
      return response.data;
    } catch (error) {
      console.error('Error saving agent config:', error);
      throw error;
    }
  }

  // Update agent params (bind/reset device id)
  async updateAgentParams(storeId: string, params: UpdateAgentParamsPayload): Promise<AgentParams> {
    try {
      const response = await api({
        method: 'PATCH',
        url: API_URLS.UPDATE_AGENT_PARAMS(storeId),
        data: params,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating agent params:', error);
      throw error;
    }
  }

  // Get agent params (used for latest runtime metadata/device binding)
  async getAgentParams(storeId: string): Promise<AgentParams> {
    try {
      const data = await apiGet(API_URLS.GET_AGENT_PARAMS(storeId));
      return data;
    } catch (error) {
      console.error('Error fetching agent params:', error);
      throw error;
    }
  }

  // Trigger agent health-check endpoint to upload latest logs
  async uploadAgentLogs(storeId: string, payload: UploadAgentLogsPayload): Promise<any> {
    try {
      const response = await api({
        method: 'PUT',
        url: API_URLS.UPLOAD_AGENT_LOGS(storeId),
        data: payload,
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading latest logs:', error);
      throw error;
    }
  }

  // Get store logs with pagination
  async getStoreLogs(storeId: string, limit = 100, offset = 0): Promise<StoreLogsResponse> {
    try {
      const data = await apiGet(API_URLS.GET_STORE_LOGS(storeId, limit, offset));
      return data;
    } catch (error) {
      console.error('Error fetching store logs:', error);
      throw error;
    }
  }

  // Get POS types
  async getPosTypes(): Promise<string[]> {
    try {
      const data = await apiGet(API_URLS.GET_POS_TYPES);
      return data || [];
    } catch (error) {
      console.error('Error fetching POS types:', error);
      throw error;
    }
  }

  // Update store
  async updateStore(storeId: string, storeData: any): Promise<Store> {
    try {
      const response = await api({
        method: 'PUT',
        url: API_URLS.UPDATE_STORE(storeId),
        data: storeData,
      });
      return response.data;
    } catch (error) {
      console.error('Error updating store:', error);
      throw error;
    }
  }

  async syncGamesToPos(storeId: string, payload: SyncGamesToPosPayload): Promise<Store> {
    try {
      const response = await api({
        method: 'PUT',
        url: API_URLS.SYNC_GAMES_TO_POS(storeId),
        data: payload,
      });
      return response.data;
    } catch (error) {
      console.error('Error syncing games to POS:', error);
      throw error;
    }
  }

  // Get activated packs for a store
  async getActivatedPacks(storeId: string, storeLocationId?: string): Promise<ActivatedPacksResponse> {
    try {
      const data = await apiGet(API_URLS.GET_ACTIVATED_PACKS(storeId, storeLocationId));
      return data || { items: [], total: 0 };
    } catch (error) {
      console.error('Error fetching activated packs:', error);
      throw error;
    }
  }

  // Get active games and packs for a store
  async getActiveGamesAndPacks(storeId: string): Promise<ActiveGamesAndPacksResponse> {
    try {
      const data = await apiGet(API_URLS.GET_ACTIVE_GAMES_AND_PACKS(storeId));
      const items = (data.items || []) as any[];
      return items.map((game: any) => {
        const normalizedGameId = normalizeIdToInt(game?.game_id);
        const normalizedCost = normalizePriceToFloat(game?.cost_per_ticket);
        const normalizedTicketsPerPack = normalizeIdToInt(game?.tickets_per_pack);

        return {
          ...game,
          game_id: normalizedGameId ?? game?.game_id,
          cost_per_ticket: normalizedCost ?? game?.cost_per_ticket ?? 0,
          tickets_per_pack: normalizedTicketsPerPack ?? game?.tickets_per_pack ?? 0,
        };
      });
    } catch (error) {
      console.error('Error fetching active games and packs:', error);
      throw error;
    }
  }

  // Get user details
  async authMeAndGetUserDetails(): Promise<User> {
    try {
      const data = await apiGet(API_URLS.AUTH_ME_AND_GET_USER_DETAILS);
      return data;
    } catch (error) {
      console.error('Error fetching user details:', error);
      throw error;
    }
  }

  // Get current user
  getCurrentUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  // Check if user is authenticated
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const refreshToken = localStorage.getItem('refreshToken');
    
    // If we have a refresh token but no valid access token, we should be able to refresh
    if (!token && refreshToken) {
      return true; // Allow authentication, let the API calls handle refresh
    }
    
    // User is authenticated if they have both tokens
    const hasTokens = !!token && !!refreshToken;
    
    return hasTokens;
  }

  // Logout
  logout() {
    // Clear tokens
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('user');
    localStorage.removeItem('userDetails');
    localStorage.clear();
  }
}

const authServiceInstance = new AuthService();

export default authServiceInstance;
